import { useState, useEffect } from "react";

type UseFetchAsyncOptions<T> = {
  fetchFn: () => Promise<T>;
  isEnabled?: boolean;
  param?: any;
};

type UseFetchAsyncResult<T> = {
  data?: T;
  isLoading: boolean;
  error?: Error;
};

export const useFetchAsync = <T>(options: UseFetchAsyncOptions<T>): UseFetchAsyncResult<T> => {
  const { fetchFn, isEnabled = true, param } = options;

  const [data, setData] = useState<T | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>(undefined);

  useEffect(() => {
    if (!isEnabled) return;

    let active = true;

    (async () => {
      setIsLoading(true);
      setError(undefined);

      try {
        const result = await fetchFn();
        if (active) setData(result);
      } catch (err) {
        if (active) setError(err instanceof Error ? err : new Error("Unexpected error"));
      } finally {
        if (active) setIsLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [fetchFn, isEnabled, param]);

  return { data, isLoading, error };
};
